import { useRef, useMemo } from "react";
import { CustomAlert, CustomModal, FormCheck, FormEntry } from "components";
import apiService from "services/api";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { ImageUploadv2 } from "components/CustomUpload/ImageUploadv2";
import { Button } from "reactstrap";
import { Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNotificationContext } from "contexts";
import { errorMessageParser } from "services/constants";
import _ from "lodash";
import { useReducer } from "react";
import { modalAction, modalInitialState, modalReducer } from "../ViewStudentPage/Tabs/reducers";
import { useThrottle } from "hooks/useThrottle";

const loadCourseOptions = (inputValue, callback) => {
	apiService.course
		.get()
		.then((courses) => courses.map((course) => ({ label: course.name, value: course.id })))
		.then(callback);
};

const initialFormState = {
	isTrialClass: false,
	courseId: "",
	centreId: "",
	hoursPurchased: "",
	membershipFee: "",
	originalCoursePrice: "",
	totalPricePaid: "",
	remarks: "",
	studentId: "",
};

const FormEntryTitle = ({ label }) => <div className="font-weight-bold display-5 text-white ml-2 mb-1">{label}</div>;

export const AddCoursePurchaseModal = ({ show, hideModal, studentId, refetch }) => {
	const { createNotification } = useNotificationContext();
	const [loading, setLoading] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, dirtyFields },
		setValue,
		setError,
		control,
		watch,
		reset,
		clearErrors,
		getValues,
	} = useForm({ reValidateMode: "onChange" });

	const extendedHideModal = () => {
		reset(initialFormState);
		hideModal();
	};

	const [confirmationAlertProps, confirmationAlertDispatch] = useReducer(modalReducer, modalInitialState);

	const [confirmationCount, setConfirmationCount] = useState(0);

	const triggerConfirmation = () => {
		confirmationAlertDispatch({ type: modalAction.OPEN });
	};

	const closeConfirmationAlert = () => {
		setConfirmationCount(0);
		confirmationAlertDispatch({ type: modalAction.CLOSE });
	};

	const confirmSubmit = () => {
		if (confirmationCount <= 5) {
			return setConfirmationCount((count) => count + 1);
		}
		if (addCourseRecord.isLoading) return;
		addCourseRecord.mutate(getValues());
	};

	const addCourseRecord = useMutation((data) => {
		setLoading(true);
		const values = getValues();
		apiService.course.purchaseRecord
			.create({
				...data,
				studentId,
				totalPricePaid: values.originalCoursePrice + values.membershipFee + values.materialPrice - values.previousDeposit - values.discount,
			})
			.then(() => {
				createNotification({
					type: "success",
					message: "Successfully added course purchase record",
				});
				refetch();
				extendedHideModal();
			})
			.catch(() => {
				createNotification({
					type: "danger",
					message: "Failed to add new course purchase record",
				});
			})
			.finally(() => setLoading(false));
	});

	const { data: centres, isLoading: isLoadingCentres } = useQuery(["centre"], () =>
		apiService.student.centres().then((centres) =>
			centres.map((centre) => ({
				label: centre.shortName,
				value: centre.id,
				data: centre,
			}))
		)
	);

	const watchReceipt = watch(["receipt", "receiptNo"]);
	const watchPriceChanges = watch(["originalCoursePrice", "membershipFee", "discount", "materialPrice", "previousDeposit"]);
	const hoursPurchased = watch("hoursPurchased");
	const hoursLeft = watch("hoursLeft");

	const totalOriginalPrice = useMemo(() => {
		const result = getValues("originalCoursePrice") + getValues("membershipFee") + getValues("materialPrice") - getValues("previousDeposit");
		if (result !== 0 && _.isNaN(result)) return "";
		return result;
	}, watchPriceChanges);

	useEffect(() => {
		clearErrors();
	}, watchReceipt);

	useEffect(() => {
		setValue("hoursLeft", hoursPurchased);
	}, [hoursPurchased]);

	return (
		<>
			<CustomAlert
				show={confirmationAlertProps.show}
				hideModal={closeConfirmationAlert}
				title="Confirm Add Course Record?"
				disabled={addCourseRecord.isLoading}
				submitButtonText={confirmationCount <= 5 ? `Confirm (${confirmationCount}/5)` : "100% Certain to continue?"}
				submitModal={confirmSubmit}
				width="500px"
				body={
					<div className="row text-left pl-5 pt-3">
						<dt className="text-info col-sm-6">Hours Purchased</dt>
						<dd className="col-sm-6">{hoursPurchased}h</dd>

						<dt className="text-info col-sm-6">Hours Left</dt>
						<dd className="col-sm-6">{hoursLeft}h</dd>

						<dt className="text-info col-sm-6">Membership Fee</dt>
						<dd className="col-sm-6">HKD{watchPriceChanges[1]}</dd>

						<dt className="text-info col-sm-6">Material Price</dt>
						<dd className="col-sm-6">HKD{watchPriceChanges[3]}</dd>

						<dt className="text-info col-sm-6">Previous Deposit</dt>
						<dd className="col-sm-6">HKD{watchPriceChanges[4]}</dd>

						<dt className="text-info col-sm-6">Original Course Price</dt>
						<dd className="col-sm-6">HKD{watchPriceChanges[0]}</dd>

						<dt className="text-info col-sm-6">After Discount</dt>
						<dd className="col-sm-6">HKD{totalOriginalPrice - watchPriceChanges[2]}</dd>

						<dt className="text-info col-sm-6">Discount</dt>
						<dd className="col-sm-6">HKD {watchPriceChanges[2]}</dd>
					</div>
				}
				fitToParent={false}
			/>
			<CustomModal
				show={show}
				hideModal={extendedHideModal}
				title="Add Course Purchase"
				triggerAlertWhenClose={isDirty}
				triggerAlertMessageWhenClose="Are you sure you want to leave? All data entered will be lost."
			>
				<form onSubmit={handleSubmit(triggerConfirmation)} className="pb-3">
					<div className="mb-2 row">
						<div className="col-sm-6">
							<FormEntryTitle label="Course" />
							<Controller
								control={control}
								rules={{
									required: {
										value: true,
										message: "Please select a course",
									},
								}}
								name="courseId"
								render={({ field: { onChange } }) => (
									<AsyncSelect
										className="react-select info mb-3"
										classNamePrefix="react-select"
										placeholder="Select course..."
										cacheOptions
										loadOptions={loadCourseOptions}
										onChange={(val) => onChange(val.value)}
										defaultOptions
									/>
								)}
							/>
							{errors.courseId && (
								<div className="text-center">
									<small className="text-danger">{errors.courseId?.message}</small>
								</div>
							)}
						</div>
						<div className="col-sm-6">
							<FormEntryTitle label="Centre" />
							<Controller
								rules={{
									required: {
										value: true,
										message: "Centre is required",
									},
								}}
								control={control}
								name="centreId"
								render={({ field: { ref, value, onChange } }) => (
									<Select
										inputRef={ref}
										className="react-select info"
										classNamePrefix="react-select"
										value={centres?.find((c) => value === c.value)}
										onChange={(val) => onChange(val.value)}
										options={centres || []}
										placeholder="Select centre...."
									/>
								)}
							/>
							{errors.centreId && (
								<div className="text-center">
									<small className="text-danger">{errors.centreId?.message}</small>
								</div>
							)}
						</div>
					</div>

					<div className="mb-3 row">
						<div className="col-12">
							<FormEntry
								register={register}
								name="hoursPurchased"
								errors={errors.hoursPurchased}
								formLabel="Hours Purchased"
								type="number"
								placeholder="Number of hours purchased"
								required={true}
								step="0.25"
								validation={{
									checkValue: (v) => v > 0 || "Hours must be greater than 0!",
								}}
							/>
							{errors.hoursPurchased && (
								<div className="text-center">
									<small className="text-danger">{errors.hoursPurchased?.message}</small>
								</div>
							)}
						</div>
						<div className="col-12">
							<FormEntry
								register={register}
								name="hoursLeft"
								errors={errors.hoursLeft}
								formLabel="Hours Left"
								type="number"
								placeholder="Leave blank if hours are not used"
								step="0.25"
								validation={{
									checkValue: (v) => v > 0 || "Hours must be greater than 0!",
								}}
							/>
							{errors.hoursLeft && (
								<div className="text-center">
									<small className="text-danger">{errors.hoursLeft?.message}</small>
								</div>
							)}
						</div>
						<div className="col-12">
							<FormEntry
								register={register}
								name="remarks"
								errors={errors.remarks}
								formLabel="Remarks"
								type="paragraph"
								placeholder="Any additional comments..."
								required={false}
							/>
						</div>
					</div>

					<div className="mb-2 mt-3 row">
						<div className="col-12">
							<div
								className="col-12 py-2 mb-4"
								style={{
									border: "2px solid #ffffff25",
									borderRadius: 8,
									backgroundColor: "#1D253B",
								}}
							>
								<div
									className="row pb-1"
									style={{
										borderBottom: "2px solid #ffffff25",
									}}
								>
									<small className="col text-info font-weight-bold">Membership Fee (HKD)</small>
									<small className="col text-info font-weight-bold">Ori Course Price (HKD)</small>
									<small className="col text-info font-weight-bold">Material Price (HKD)</small>
									<small className="col text-info font-weight-bold">Previous Deposit (HKD)</small>
									<small className="col text-info font-weight-bold">Total Ori Fee (HKD)</small>
									<small className="col text-info font-weight-bold">Total Price Paid (HKD)</small>
									<small className="col text-info font-weight-bold">Discount (HKD)</small>
								</div>
								<div className="row pt-2">
									<span className="col">
										<FormEntry
											register={register}
											name="membershipFee"
											errors={errors.membershipFee}
											type="number"
											required={true}
											noLabel
										/>
									</span>
									<span className="col">
										<FormEntry
											register={register}
											name="originalCoursePrice"
											errors={errors.originalCoursePrice}
											type="number"
											required={true}
											noLabel
										/>
									</span>
									<span className="col">
										<FormEntry
											register={register}
											name="materialPrice"
											errors={errors.materialPrice}
											type="number"
											required={true}
											noLabel
										/>
									</span>
									<span className="col">
										<FormEntry
											register={register}
											name="previousDeposit"
											errors={errors.previousDeposit}
											noLabel
											type="number"
											required={true}
										/>
									</span>
									<div className="col mt-2">
										<span className="text-white">{totalOriginalPrice}</span>
									</div>
									<span className="col mt-2">
										<span className="text-white">
											{useMemo(() => {
												const totalPricePaid = totalOriginalPrice - getValues("discount");
												if (totalPricePaid !== 0 && _.isNaN(totalPricePaid)) return "";
												return totalPricePaid;
											}, watchPriceChanges)}
										</span>
									</span>
									<div className="col d-flex align-items-center">
										<FormEntry
											register={register}
											name="discount"
											step={0.1}
											errors={errors.discount}
											type="number"
											noLabel
											required={true}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="mb-3 row">
						<div className="col-sm-6 align-items-center d-flex">
							<FormCheck register={register} name="isTrialClass" formLabel="Is this a Trial Class?" error={errors.isTrialClass} />
						</div>
					</div>

					<div className="d-flex justify-content-end">
						<Button color="success" type="submit" disabled={loading}>
							Submit
						</Button>
					</div>
				</form>
			</CustomModal>
		</>
	);
};
